import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import { mergeRight } from 'ramda';
import { assert } from '../lib/utils';

const css = require('./popup.css').default;

/**
 * 當 src 是網頁時，使用固定尺寸
 * 當 src 是圖片時，符合圖片的尺寸
 */
export default function ({ src, screenViewport, onClose }: {
    src: string,
    screenViewport: viewport,
    onClose: () => void,
}) {
    const [imgSize, setImgSize] = useState<size | null>(null);
    const [close, setClose] = useState(false);
    const box = useRef<HTMLDivElement>(null);

    const position: React.CSSProperties = (() => {
        if (isHtml(src) || isVideo(src)) {
            return {
                width: screenViewport.width,
                height: screenViewport.height - 40,
                left: 0,
                top: `${calcTop(screenViewport.height - 40, screenViewport)}px`,
            }
        } else {
            const wh: React.CSSProperties = {
                width: 'fit-content',
                height: 'fit-content',
            }
            if (box.current === null || imgSize === null) {
                return mergeRight(wh, {
                    opacity: 0,
                });
            } else {
                return mergeRight(wh, {
                    left: `${(screenViewport.width - box.current.offsetWidth) / 2}px`,
                    top: `${calcTop(box.current.offsetHeight, screenViewport)}px`,
                });
            }
        }
    })();

    const closingStyle: React.CSSProperties = {
        top: '-50px',
        opacity: 0,
        transitionProperty: 'top, opacity',
        transitionDuration: '0.2s',
        transitionTimingFunction: 'ease-in',
        pointerEvents: 'none',
    }

    const style: React.CSSProperties = close ? mergeRight(position, closingStyle) : position;

    return (
    <div ref={box} id={css.main} style={style} onTransitionEnd={onClose}>
        {(() => {
            if (isDom(src)) {
                return <Dom src={src}/>
            } else if (isHtml(src)) {
                return <iframe src={src}/>;
            } else if (isVideo(src)) {
                return <Video src={src}/>;
            } else {
                return <Img src={src} onSize={setImgSize}/>;
            }
        })()}
        <div id={css.cmd}>
            <button onClick={() => setClose(true)}>關閉</button>
        </div>
    </div>);
}

function Img({ src, onSize }: {
    src: string,
    onSize: (size: size) => void,
}) {
    const [loaded, setLoaded] = useState(false);

    if (loaded) {
        return <img src={src} onLoad={e => {
            assert(e.target instanceof HTMLImageElement, '');
            onSize({
                width: e.target.naturalWidth,
                height: e.target.naturalHeight,
            });
        }}/>;
    } else {
        return (
        <>
            <img src='/imgs/loading.gif' onLoad={e => {
                assert(e.target instanceof HTMLImageElement, '');
                onSize({
                    width: e.target.naturalWidth,
                    height: e.target.naturalHeight,
                });
            }}/>
            <img src={src} style={{ display: 'none' }} onLoad={() => setLoaded(true)}/>
        </>);
    }
}

function Video({ src }: {
    src: string,
}) {
    return (
    <video controls>
        <source src={src} type="video/mp4"/>
        <track kind="subtitles" src={src.replace(/\.mp4$/, '.vtt')} default/>
        Sorry, your browser doesn't support embedded videos.
    </video>);
}

function Dom({ src }: {
    src: string,
}) {
    const div = useRef<HTMLDivElement>(null);

    useEffect(() => {
        fetch(src)
        .then(res => res.text())
        .then(html => {
            assert(div.current !== null, '');
            // ref: https://github.com/christo-pr/dangerously-set-html-content
            // Create a 'tiny' document and parse the html string
            const slotHtml = document.createRange().createContextualFragment(html);
            div.current.innerHTML = '';
            div.current.appendChild(slotHtml);
        })
    }, []);

    return (
    <div
        ref={div}
        data-page='__ebook_page'
        data-dir={basedir(src)}
    />);
}

function isHtml(path: string) {
    return path.toLowerCase().endsWith('.html') || path.toLowerCase().endsWith('.htm');
}

function isDom(path: string) {
    return isHtml(path) && path.toLowerCase().includes('.dom.');
}

function isVideo(path: string) {
    return path.toLowerCase().endsWith('.mp4');
}

function basedir(path: string) {
    return path.split('/').slice(0, -1).join('/');
}

function calcTop(height: number, screenViewport: viewport) {
    if (height < screenViewport.height) {
        if (screenViewport.height - height >= 50) {
            // 第二次除以2，是把 popup 的位置往上拉一點
            return (((screenViewport.height - height) / 2) / 2) + screenViewport.y;
        } else {
            return screenViewport.y;
        }
    } else {
        return screenViewport.y;
    }
}

type size = {
    width: number,
    height: number,
}

type viewport = {
    width: number,
    height: number,
    x: number,
    y: number,
}
